/**
 * Settings sidebar panel
 */

import { useCallback, useRef, useState, type ChangeEvent } from 'react';
import type { Settings, ModelName, McpServerConfig, VectorStoreCache, VectorStore, VectorStoreFile } from '../../types';
import {
  AVAILABLE_MODELS,
  CUSTOM_MODEL_OPTION,
  getReasoningEfforts,
  supportsSamplingControls,
  VERBOSITY_OPTIONS,
  REASONING_SUMMARY_OPTIONS,
  MESSAGE_RENDER_MODE_OPTIONS,
  THEME_OPTIONS,
  DEFAULT_SETTINGS,
  DEFAULT_OUTPUT_TEXT_ZOOM,
  OUTPUT_TEXT_ZOOM_MAX,
  OUTPUT_TEXT_ZOOM_MIN,
  OUTPUT_TEXT_ZOOM_STEP,
} from '../../types';
import { McpServerSettings } from '../McpServerSettings';
import { FileSearchSettings } from '../FileSearchSettings';
import type { ThreadImportResult } from '../../utils/threadExport';
import './SettingsSidebar.css';

/** Handler type for checkbox change events */
type CheckboxChangeHandler = (field: keyof Settings) => (e: ChangeEvent<HTMLInputElement>) => void;
type OptionalNumberField = 'temperature' | 'topP';

const OUTPUT_TEXT_ZOOM_SLIDER_MIN = 0;
const OUTPUT_TEXT_ZOOM_SLIDER_MAX = 100;
const OUTPUT_TEXT_ZOOM_SLIDER_CENTER = 50;

function clampOutputTextZoom(value: number): number {
  return Math.min(Math.max(value, OUTPUT_TEXT_ZOOM_MIN), OUTPUT_TEXT_ZOOM_MAX);
}

function snapOutputTextZoom(value: number): number {
  return Math.round(value / OUTPUT_TEXT_ZOOM_STEP) * OUTPUT_TEXT_ZOOM_STEP;
}

function sliderPositionToOutputTextZoom(position: number): number {
  if (position <= OUTPUT_TEXT_ZOOM_SLIDER_CENTER) {
    const lowerHalfProgress = position / OUTPUT_TEXT_ZOOM_SLIDER_CENTER;
    return snapOutputTextZoom(
      OUTPUT_TEXT_ZOOM_MIN +
        (DEFAULT_OUTPUT_TEXT_ZOOM - OUTPUT_TEXT_ZOOM_MIN) * lowerHalfProgress
    );
  }

  const upperHalfProgress =
    (position - OUTPUT_TEXT_ZOOM_SLIDER_CENTER) /
    (OUTPUT_TEXT_ZOOM_SLIDER_MAX - OUTPUT_TEXT_ZOOM_SLIDER_CENTER);

  return snapOutputTextZoom(
    DEFAULT_OUTPUT_TEXT_ZOOM +
      (OUTPUT_TEXT_ZOOM_MAX - DEFAULT_OUTPUT_TEXT_ZOOM) * upperHalfProgress
  );
}

function outputTextZoomToSliderPosition(zoom: number): number {
  const clampedZoom = clampOutputTextZoom(zoom);

  if (clampedZoom <= DEFAULT_OUTPUT_TEXT_ZOOM) {
    const lowerHalfProgress =
      (clampedZoom - OUTPUT_TEXT_ZOOM_MIN) /
      (DEFAULT_OUTPUT_TEXT_ZOOM - OUTPUT_TEXT_ZOOM_MIN);

    return OUTPUT_TEXT_ZOOM_SLIDER_MIN + lowerHalfProgress * OUTPUT_TEXT_ZOOM_SLIDER_CENTER;
  }

  const upperHalfProgress =
    (clampedZoom - DEFAULT_OUTPUT_TEXT_ZOOM) /
    (OUTPUT_TEXT_ZOOM_MAX - DEFAULT_OUTPUT_TEXT_ZOOM);

  return (
    OUTPUT_TEXT_ZOOM_SLIDER_CENTER +
    upperHalfProgress * (OUTPUT_TEXT_ZOOM_SLIDER_MAX - OUTPUT_TEXT_ZOOM_SLIDER_CENTER)
  );
}

interface SettingsSidebarProps {
  /** Whether the sidebar is open */
  isOpen: boolean;
  /** Handler to close the sidebar */
  onClose: () => void;
  /** Current settings */
  settings: Settings;
  /** Handler to update settings */
  onUpdateSettings: (updates: Partial<Settings>) => void;
  /** Handler to clear all stored data */
  onClearStoredData: () => void;
  /** Number of saved threads available for export */
  savedThreadCount?: number;
  /** Handler to export saved threads to a JSON file */
  onExportThreads?: () => void;
  /** Handler to import saved threads from a JSON file */
  onImportThreadsFile?: (file: File) => Promise<ThreadImportResult> | ThreadImportResult;
  /** Vector store cache */
  vectorStoreCache: VectorStoreCache;
  /** Update cached vector stores list */
  setVectorStores: (stores: VectorStore[]) => void;
  /** Update cached files for a store */
  setStoreFiles: (storeId: string, files: VectorStoreFile[]) => void;
  /** Set loading state for a store's files */
  setStoreFilesLoading: (storeId: string, isLoading: boolean) => void;
}

/**
 * Slide-in settings panel from the right side
 */
export function SettingsSidebar({
  isOpen,
  onClose,
  settings,
  onUpdateSettings,
  onClearStoredData,
  savedThreadCount = 0,
  onExportThreads,
  onImportThreadsFile,
  vectorStoreCache,
  setVectorStores,
  setStoreFiles,
  setStoreFilesLoading,
}: SettingsSidebarProps) {
  const isCustomModel = !AVAILABLE_MODELS.includes(settings.modelName);
  const [showCustomModelInput, setShowCustomModelInput] = useState(isCustomModel);
  const isCustomTitleModel = settings.titleModelName != null && settings.titleModelName !== '' && !AVAILABLE_MODELS.includes(settings.titleModelName);
  const [showCustomTitleModelInput, setShowCustomTitleModelInput] = useState(isCustomTitleModel);
  const [importStatus, setImportStatus] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const importInputRef = useRef<HTMLInputElement>(null);

  const availableReasoningEfforts = getReasoningEfforts(settings.modelName);
  const samplingControlsSupported = supportsSamplingControls(
    settings.modelName,
    settings.reasoningEffort
  );
  const outputTextZoomSliderValue = outputTextZoomToSliderPosition(
    settings.outputTextZoom ?? DEFAULT_OUTPUT_TEXT_ZOOM
  );

  const handleInputChange = useCallback(
    (field: keyof Settings) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const { value } = e.target;
      // Required string fields store empty string; optional fields convert empty to undefined
      const requiredStringFields: Array<keyof Settings> = ['endpoint', 'apiKey', 'deploymentName'];
      onUpdateSettings({ [field]: requiredStringFields.includes(field) ? value : value || undefined } as Partial<Settings>);
    },
    [onUpdateSettings]
  );

  const handleCheckboxChange: CheckboxChangeHandler = useCallback(
    (field: keyof Settings) => (e: ChangeEvent<HTMLInputElement>) => {
      onUpdateSettings({ [field]: e.target.checked });
    },
    [onUpdateSettings]
  );

  const handleSliderChange = useCallback(
    (field: keyof Settings) => (e: ChangeEvent<HTMLInputElement>) => {
      const parsed = parseInt(e.target.value, 10);
      if (!isNaN(parsed)) {
        onUpdateSettings({ [field]: parsed });
      }
    },
    [onUpdateSettings]
  );

  const handleOptionalNumberChange = useCallback(
    (field: OptionalNumberField) => (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      if (value === '') {
        onUpdateSettings({ [field]: undefined } as Partial<Settings>);
        return;
      }

      const parsed = Number(value);
      if (!Number.isNaN(parsed)) {
        onUpdateSettings({ [field]: parsed } as Partial<Settings>);
      }
    },
    [onUpdateSettings]
  );

  const handleOutputTextZoomChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const parsed = Number(e.target.value);
      if (!Number.isNaN(parsed)) {
        onUpdateSettings({ outputTextZoom: sliderPositionToOutputTextZoom(parsed) });
      }
    },
    [onUpdateSettings]
  );

  const handleMcpServersChange = useCallback(
    (servers: McpServerConfig[]) => {
      onUpdateSettings({ mcpServers: servers });
    },
    [onUpdateSettings]
  );

  const handleImportClick = useCallback(() => {
    importInputRef.current?.click();
  }, []);

  const handleImportChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file || !onImportThreadsFile) {
        return;
      }

      setImportStatus('Importing threads...');
      setImportError(null);
      try {
        const result = await onImportThreadsFile(file);
        const changed = result.imported + result.replaced;
        setImportStatus(
          `Imported ${result.imported}, replaced ${result.replaced}, skipped ${result.skipped}.`
        );
        if (changed === 0 && result.skipped === 0) {
          setImportStatus('No threads found in import file.');
        }
      } catch (error) {
        setImportStatus(null);
        setImportError(error instanceof Error ? error.message : 'Failed to import threads.');
      }
    },
    [onImportThreadsFile]
  );

  const handleModelChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      if (value === CUSTOM_MODEL_OPTION) {
        setShowCustomModelInput(true);
        return;
      }
      setShowCustomModelInput(false);
      const newModel = value as ModelName;
      const newAvailableEfforts = getReasoningEfforts(newModel);
      const updates: Partial<Settings> = { modelName: newModel };
      if (
        settings.reasoningEffort &&
        !newAvailableEfforts.includes(settings.reasoningEffort)
      ) {
        updates.reasoningEffort = undefined;
      }
      onUpdateSettings(updates);
    },
    [settings.reasoningEffort, onUpdateSettings]
  );

  const handleCustomModelChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newModel = e.target.value;
      const newAvailableEfforts = getReasoningEfforts(newModel);
      const updates: Partial<Settings> = { modelName: newModel };
      if (
        settings.reasoningEffort &&
        !newAvailableEfforts.includes(settings.reasoningEffort)
      ) {
        updates.reasoningEffort = undefined;
      }
      onUpdateSettings(updates);
    },
    [settings.reasoningEffort, onUpdateSettings]
  );

  const handleTitleModelChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      if (value === CUSTOM_MODEL_OPTION) {
        setShowCustomTitleModelInput(true);
        return;
      }
      setShowCustomTitleModelInput(false);
      onUpdateSettings({ titleModelName: value || undefined });
    },
    [onUpdateSettings]
  );

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  if (!isOpen) {
    return <></>;
  }

  return (
    <div className="settings-overlay" onClick={handleOverlayClick}>
      <div className="settings-sidebar">
        <div className="settings-sidebar__header">
          <h2 className="settings-sidebar__title">Settings</h2>
          <button
            className="settings-sidebar__close"
            onClick={onClose}
            aria-label="Close settings"
          >
            ✕
          </button>
        </div>

        <div className="settings-sidebar__content">
          {/* Storage Settings */}
          <section className="settings-section settings-section--storage">
            <h3 className="settings-section__title">Storage</h3>

            <div className="settings-field">
              <label className="settings-field__checkbox-wrapper">
                <input
                  id="noLocalStorage"
                  type="checkbox"
                  className="settings-field__checkbox"
                  checked={settings.noLocalStorage || false}
                  onChange={handleCheckboxChange('noLocalStorage')}
                />
                <span className="settings-field__checkbox-label">Don't save settings</span>
              </label>
              <span className="settings-field__hint">
                When enabled, settings are not saved and must be re-entered each session.
              </span>
            </div>

            <div className="settings-field">
              <span className="settings-field__label">Saved Threads</span>
              <div className="settings-storage__json-actions">
                <button
                  className="settings-storage__json-btn"
                  onClick={onExportThreads}
                  disabled={!onExportThreads || savedThreadCount === 0}
                  title={savedThreadCount === 0 ? 'No saved threads to export' : 'Export saved threads to JSON'}
                >
                  Export JSON
                </button>
                <button
                  className="settings-storage__json-btn"
                  onClick={handleImportClick}
                  disabled={!onImportThreadsFile}
                  title="Import saved threads from JSON"
                >
                  Import JSON
                </button>
                <input
                  ref={importInputRef}
                  className="settings-storage__file-input"
                  type="file"
                  accept="application/json,.json"
                  onChange={handleImportChange}
                />
              </div>
              <span className="settings-field__hint">
                Export or import saved chat history only. Settings and API keys are not included.
              </span>
              {(importStatus || importError) && (
                <span
                  className={`settings-storage__import-message${
                    importError ? ' settings-storage__import-message--error' : ''
                  }`}
                  role={importError ? 'alert' : 'status'}
                >
                  {importError ?? importStatus}
                </span>
              )}
            </div>
          </section>

          {/* Appearance Settings */}
          <section className="settings-section">
            <h3 className="settings-section__title">Appearance</h3>

            <div className="settings-field">
              <span className="settings-field__label">Theme</span>
              <div className="settings-field__radio-group">
                {THEME_OPTIONS.map((theme) => (
                  <label key={theme} className="settings-field__radio-wrapper">
                    <input
                      type="radio"
                      name="theme"
                      className="settings-field__radio"
                      value={theme}
                      checked={settings.theme === theme}
                      onChange={() => onUpdateSettings({ theme })}
                    />
                    <span className="settings-field__radio-label">
                      {theme.charAt(0).toUpperCase() + theme.slice(1)}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="settings-field">
              <label className="settings-field__label" htmlFor="outputTextZoom">
                Output Text Zoom: {(settings.outputTextZoom ?? DEFAULT_OUTPUT_TEXT_ZOOM).toString()}%
              </label>
              <input
                id="outputTextZoom"
                type="range"
                className="settings-field__slider"
                value={outputTextZoomSliderValue}
                onChange={handleOutputTextZoomChange}
                min={OUTPUT_TEXT_ZOOM_SLIDER_MIN.toString()}
                max={OUTPUT_TEXT_ZOOM_SLIDER_MAX.toString()}
                step="1"
              />
              <div className="settings-field__slider-labels">
                <span>{OUTPUT_TEXT_ZOOM_MIN}%</span>
                <span>{DEFAULT_OUTPUT_TEXT_ZOOM}%</span>
                <span>{OUTPUT_TEXT_ZOOM_MAX}%</span>
              </div>
              <span className="settings-field__hint">
                Scales assistant output content, including markdown tables and code blocks.
              </span>
            </div>
          </section>

          {/* Required Settings */}
          <section className="settings-section">
            <h3 className="settings-section__title">Required</h3>

            <div className="settings-field">
              <label className="settings-field__label" htmlFor="endpoint">
                Endpoint URL
              </label>
              <input
                id="endpoint"
                type="url"
                className="settings-field__input"
                value={settings.endpoint}
                onChange={handleInputChange('endpoint')}
                placeholder="https://your-resource.openai.azure.com"
              />
            </div>

            <div className="settings-field">
              <label className="settings-field__label" htmlFor="apiKey">
                API Key
              </label>
              <input
                id="apiKey"
                type="password"
                className="settings-field__input"
                value={settings.apiKey}
                onChange={handleInputChange('apiKey')}
                placeholder="Enter your API key"
              />
              {settings.noLocalStorage ? (
                <span className="settings-field__hint">
                  Storage disabled — credentials will not be saved.
                </span>
              ) : (
                <span className="settings-field__hint settings-field__hint--warning">
                  Encrypted before being saved in browser storage. Use only on trusted devices.
                </span>
              )}
            </div>

            <div className="settings-field">
              <label className="settings-field__label" htmlFor="modelName">
                Model
              </label>
              <select
                id="modelName"
                className="settings-field__select"
                value={showCustomModelInput ? CUSTOM_MODEL_OPTION : settings.modelName}
                onChange={handleModelChange}
              >
                {AVAILABLE_MODELS.map((model) => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
                <option value={CUSTOM_MODEL_OPTION}>Custom…</option>
              </select>
              {showCustomModelInput && (
                <input
                  id="customModelName"
                  type="text"
                  className="settings-field__input"
                  value={settings.modelName}
                  onChange={handleCustomModelChange}
                  placeholder="Enter model or deployment name"
                  autoFocus
                />
              )}
            </div>

            <div className="settings-field">
              <label className="settings-field__label" htmlFor="deploymentName">
                Deployment Name
              </label>
              <input
                id="deploymentName"
                type="text"
                className="settings-field__input"
                value={settings.deploymentName}
                onChange={handleInputChange('deploymentName')}
                placeholder={settings.modelName}
              />
              <span className="settings-field__hint">
                Defaults to model name if empty
              </span>
            </div>
          </section>

          {/* Tools Settings */}
          <section className="settings-section">
            <h3 className="settings-section__title">Tools</h3>
            <span className="settings-section__notice">
              Built-in tools may incur additional costs.
            </span>

            <div className="settings-field">
              <label className="settings-field__checkbox-wrapper">
                <input
                  id="parallelToolCallsEnabled"
                  type="checkbox"
                  className="settings-field__checkbox"
                  checked={settings.parallelToolCallsEnabled || false}
                  onChange={handleCheckboxChange('parallelToolCallsEnabled')}
                />
                <span className="settings-field__checkbox-label">Parallel Tool Calls</span>
              </label>
              <span className="settings-field__hint">
                Allow the model to call multiple tools at the same time.
              </span>
            </div>

            <div className="settings-field">
              <label className="settings-field__checkbox-wrapper">
                <input
                  id="webSearchEnabled"
                  type="checkbox"
                  className="settings-field__checkbox"
                  checked={settings.webSearchEnabled || false}
                  onChange={handleCheckboxChange('webSearchEnabled')}
                />
                <span className="settings-field__checkbox-label">Web Search</span>
              </label>
              <span className="settings-field__hint">
                Ground responses with real-time web data via Bing.
              </span>
            </div>

            {settings.webSearchEnabled && (
              <div className="settings-field">
                <label className="settings-field__label" htmlFor="webSearchContextSize">
                  Search Context Size
                </label>
                <select
                  id="webSearchContextSize"
                  className="settings-field__select"
                  value={settings.webSearchContextSize || ''}
                  onChange={handleInputChange('webSearchContextSize')}
                >
                  <option value="">Default</option>
                  <option value="low">low</option>
                  <option value="medium">medium</option>
                  <option value="high">high</option>
                </select>
                <span className="settings-field__hint">
                  Lower values reduce token usage and help avoid rate limits.
                </span>
              </div>
            )}

            <div className="settings-field">
              <label className="settings-field__checkbox-wrapper">
                <input
                  id="codeInterpreterEnabled"
                  type="checkbox"
                  className="settings-field__checkbox"
                  checked={settings.codeInterpreterEnabled || false}
                  onChange={handleCheckboxChange('codeInterpreterEnabled')}
                />
                <span className="settings-field__checkbox-label">Code Interpreter</span>
              </label>
              <span className="settings-field__hint">
                Execute Python code in a sandboxed environment.
              </span>
            </div>

            <div className="settings-field">
              <label className="settings-field__checkbox-wrapper">
                <input
                  id="fileSearchEnabled"
                  type="checkbox"
                  className="settings-field__checkbox"
                  checked={settings.fileSearchEnabled || false}
                  onChange={handleCheckboxChange('fileSearchEnabled')}
                />
                <span className="settings-field__checkbox-label">File Search</span>
              </label>
              <span className="settings-field__hint">
                Search uploaded documents using vector stores.
              </span>
            </div>

            {settings.fileSearchEnabled && (
              <FileSearchSettings
                settings={settings}
                onUpdateSettings={onUpdateSettings}
                vectorStoreCache={vectorStoreCache}
                setVectorStores={setVectorStores}
                setStoreFiles={setStoreFiles}
                setStoreFilesLoading={setStoreFilesLoading}
              />
            )}
          </section>

          {/* MCP Servers */}
          <section className="settings-section">
            <h3 className="settings-section__title">MCP Servers</h3>
            <span className="settings-field__hint" style={{ marginBottom: '12px', display: 'block' }}>
              Connect to remote Model Context Protocol (MCP) servers to extend model capabilities.
            </span>
            <McpServerSettings
              servers={settings.mcpServers || []}
              onUpdateServers={handleMcpServersChange}
            />
          </section>

          {/* Optional Settings */}
          <section className="settings-section">
            <h3 className="settings-section__title">Optional</h3>

            <div className="settings-field">
              <label className="settings-field__label" htmlFor="reasoningEffort">
                Reasoning Effort
              </label>
              <select
                id="reasoningEffort"
                className="settings-field__select"
                value={settings.reasoningEffort || ''}
                onChange={handleInputChange('reasoningEffort')}
              >
                <option value="">Default</option>
                {availableReasoningEfforts.map((effort) => (
                  <option key={effort} value={effort}>
                    {effort}
                  </option>
                ))}
              </select>
            </div>

            {samplingControlsSupported && (
              <>
                <div className="settings-field">
                  <label className="settings-field__checkbox-wrapper">
                    <input
                      id="temperatureEnabled"
                      type="checkbox"
                      className="settings-field__checkbox"
                      checked={settings.temperatureEnabled || false}
                      onChange={handleCheckboxChange('temperatureEnabled')}
                    />
                    <span className="settings-field__checkbox-label">Set Temperature</span>
                  </label>
                  {settings.temperatureEnabled && (
                    <>
                      <label className="settings-field__label" htmlFor="temperature">
                        Temperature: {(settings.temperature ?? 1).toFixed(1)}
                      </label>
                      <input
                        id="temperature"
                        type="range"
                        className="settings-field__slider"
                        value={settings.temperature ?? 1}
                        onChange={handleOptionalNumberChange('temperature')}
                        min="0"
                        max="2"
                        step="0.1"
                      />
                      <div className="settings-field__slider-labels">
                        <span>0.0</span>
                        <span>2.0</span>
                      </div>
                    </>
                  )}
                  <span className="settings-field__hint">
                    Off by default so the API uses its built-in temperature default.
                  </span>
                </div>

                <div className="settings-field">
                  <label className="settings-field__checkbox-wrapper">
                    <input
                      id="topPEnabled"
                      type="checkbox"
                      className="settings-field__checkbox"
                      checked={settings.topPEnabled || false}
                      onChange={handleCheckboxChange('topPEnabled')}
                    />
                    <span className="settings-field__checkbox-label">Set Top P</span>
                  </label>
                  {settings.topPEnabled && (
                    <>
                      <label className="settings-field__label" htmlFor="topP">
                        Top P: {(settings.topP ?? 1).toFixed(2)}
                      </label>
                      <input
                        id="topP"
                        type="range"
                        className="settings-field__slider"
                        value={settings.topP ?? 1}
                        onChange={handleOptionalNumberChange('topP')}
                        min="0"
                        max="1"
                        step="0.05"
                      />
                      <div className="settings-field__slider-labels">
                        <span>0.00</span>
                        <span>1.00</span>
                      </div>
                    </>
                  )}
                  <span className="settings-field__hint">
                    Off by default so the API uses its built-in top_p default.
                  </span>
                </div>
              </>
            )}

            <div className="settings-field">
              <label className="settings-field__label" htmlFor="reasoningSummary">
                Reasoning Summary
              </label>
              <select
                id="reasoningSummary"
                className="settings-field__select"
                value={settings.reasoningSummary || ''}
                onChange={handleInputChange('reasoningSummary')}
              >
                <option value="">Default</option>
                {REASONING_SUMMARY_OPTIONS.map((summary) => (
                  <option key={summary} value={summary}>
                    {summary}
                  </option>
                ))}
              </select>
              <span className="settings-field__hint">
                Set to "detailed" for reasoning to appear in stream
              </span>
            </div>

            <div className="settings-field">
              <label className="settings-field__label" htmlFor="verbosity">
                Verbosity
              </label>
              <select
                id="verbosity"
                className="settings-field__select"
                value={settings.verbosity || ''}
                onChange={handleInputChange('verbosity')}
              >
                <option value="">Default</option>
                {VERBOSITY_OPTIONS.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>

            <div className="settings-field">
              <label className="settings-field__label" htmlFor="developerInstructions">
                Developer Instructions
              </label>
              <textarea
                id="developerInstructions"
                className="settings-field__textarea"
                value={settings.developerInstructions || ''}
                onChange={handleInputChange('developerInstructions')}
                placeholder="System prompt / developer instructions..."
                rows={4}
              />
            </div>

            <div className="settings-field">
              <label className="settings-field__label" htmlFor="messageRenderMode">
                Message Render Mode
              </label>
              <select
                id="messageRenderMode"
                className="settings-field__select"
                value={settings.messageRenderMode}
                onChange={handleInputChange('messageRenderMode')}
              >
                {MESSAGE_RENDER_MODE_OPTIONS.map((mode) => (
                  <option key={mode} value={mode}>
                    {mode === 'markdown' ? 'Markdown (rendered)' : 
                     mode === 'plaintext' ? 'Plain Text' : 
                     'Code Block'}
                  </option>
                ))}
              </select>
              <span className="settings-field__hint">
                How assistant messages are displayed. Can be overridden per message.
              </span>
            </div>
          </section>

          {/* API Limits */}
          <section className="settings-section">
            <h3 className="settings-section__title">API Limits</h3>

            <div className="settings-field">
              <label className="settings-field__checkbox-wrapper">
                <input
                  id="maxOutputTokensEnabled"
                  type="checkbox"
                  className="settings-field__checkbox"
                  checked={settings.maxOutputTokensEnabled || false}
                  onChange={handleCheckboxChange('maxOutputTokensEnabled')}
                />
                <span className="settings-field__checkbox-label">Limit Max Output Tokens</span>
              </label>
              <span className="settings-field__hint">
                When disabled, no limit is sent (null). Enable to set a specific limit.
              </span>
            </div>

            {settings.maxOutputTokensEnabled && (
              <div className="settings-field">
                <label className="settings-field__label" htmlFor="maxOutputTokens">
                  Max Output Tokens: {(settings.maxOutputTokens ?? DEFAULT_SETTINGS.maxOutputTokens!).toLocaleString()}
                </label>
                <input
                  id="maxOutputTokens"
                  type="range"
                  className="settings-field__slider"
                  value={settings.maxOutputTokens ?? DEFAULT_SETTINGS.maxOutputTokens}
                  onChange={handleSliderChange('maxOutputTokens')}
                  min="1000"
                  max="128000"
                  step="1000"
                />
                <div className="settings-field__slider-labels">
                  <span>1K</span>
                  <span>128K</span>
                </div>
              </div>
            )}
          </section>

          {/* Clear Data */}
          <section className="settings-section settings-section--clear">

            {/* History Settings */}
            <div className="settings-field">
              <label className="settings-field__label" htmlFor="titleModelName">
                Title Generation Model
              </label>
              <select
                id="titleModelName"
                className="settings-field__select"
                value={showCustomTitleModelInput ? CUSTOM_MODEL_OPTION : (settings.titleModelName ?? '')}
                onChange={handleTitleModelChange}
              >
                <option value="">Same as chat model (default)</option>
                <option value={CUSTOM_MODEL_OPTION}>Custom…</option>
              </select>
              {showCustomTitleModelInput && (
                <input
                  id="customTitleModelName"
                  type="text"
                  className="settings-field__input"
                  value={settings.titleModelName ?? ''}
                  onChange={handleInputChange('titleModelName')}
                  placeholder="Enter model or deployment name"
                  autoFocus
                />
              )}
              <span className="settings-field__hint">
                Model used to auto-generate thread titles. Defaults to the chat model.
              </span>
            </div>
          </section>

          {/* Clear Data */}
          <section className="settings-section settings-section--clear">
            <div className="settings-field">
              <label className="settings-field__checkbox-wrapper">
                <input
                  id="mobileBackgroundStreamingEnabled"
                  type="checkbox"
                  className="settings-field__checkbox"
                  checked={settings.mobileBackgroundStreamingEnabled ?? true}
                  onChange={handleCheckboxChange('mobileBackgroundStreamingEnabled')}
                />
                <span className="settings-field__checkbox-label">Run in background on mobile</span>
              </label>
              <span className="settings-field__hint">
                Best effort: when the browser is hidden on mobile, keep streaming in the thread so
                you can resume when you return. Some browsers may still pause background work.
              </span>
            </div>

            <div className="settings-field">
              <button
                type="button"
                className="settings-storage__clear-btn"
                onClick={onClearStoredData}
              >
                Clear Saved Data
              </button>
              <span className="settings-field__hint">
                Remove all saved settings from this browser.
              </span>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
